import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
    EmployeeCriteriaSelectionComponent,
    EmployeeSelectionCriteriaFields,
} from '@app/components/employee-criteria-selection/employee-criteria-selection.component';
import { PaginationData } from '@app/interfaces';
import { Announcement } from '@app/models/company/announcement.model';
import { EmployeeFilter } from '@app/models/employee/employee-filter.model';
import { Employee } from '@app/models/employee/employee.model';
import { AnnouncementForm } from '@app/modules/forms/dashboard/components';
import { EmployeeFilterPreviewService, NotifyService } from '@app/services';
import { AuthService } from '@app/services/auth.service';

const ITEMS_PER_PAGE = 25;

const setNoFilters = { departments: [], offices: [], employmentTypes: [], jobs: [] };

@Component({
    styleUrls: ['./company-announcement-store.style.scss'],
    templateUrl: './company-announcement-store.view.template.html',
})
export class CompanyAnnouncementStoreView {
    employeeFilter: EmployeeFilter = new EmployeeFilter();
    isLoading = false;
    announcement: Announcement;
    sendToAll = true;
    @ViewChild('employeeCriteriaSelection', { static: false })
    employeeCriteriaSelection: EmployeeCriteriaSelectionComponent;

    @ViewChild('announcementForm', { static: false }) announcementForm: AnnouncementForm;
    employeesToPreview: Employee[] = [];
    showEmployeesMatched = false;
    testAnnouncementSent = false;
    employerFilterPreviewPagination: PaginationData = {
        page: 1,
        total: 0,
        perPage: ITEMS_PER_PAGE,
    };

    constructor(
        public auth: AuthService,
        private router: Router,
        private notify: NotifyService,
        private employeeFilterPreviewService: EmployeeFilterPreviewService
    ) {}

    async onSave(announcementForm: AnnouncementForm): Promise<void> {
        // Submit the form to run validation
        this.announcementForm.submit();

        if (this.announcementForm.form.invalid) {
            return;
        }

        this.isLoading = true;

        await this.attachEmployeeFilters();

        this.announcement = announcementForm.announcement;
        this.announcement.employeeFilterId = this.employeeFilter.id;

        try {
            await this.announcement.param('company', this.auth.company.id).save();
            this.isLoading = false;
            this.notify.success('dashboard.announcementSent');
            this.redirect();
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err || 'dashboard.sendAnnouncementError');
        }
    }

    async previewSelectedEmployees(): Promise<void> {
        await this.attachEmployeeFilters();

        const { employeesToPreview, totalEmployees } = await this.employeeFilterPreviewService.preview(
            this.employeeFilter,
            this.auth.company
        );

        this.employeesToPreview = employeesToPreview;

        this.employerFilterPreviewPagination.total = totalEmployees;
    }

    showPreviewOfSelectedEmployees(): void {
        this.isLoading = true;

        this.previewSelectedEmployees();

        this.isLoading = false;

        this.showEmployeesMatched = true;
    }

    async changePage(newPage: number): Promise<void> {
        this.employerFilterPreviewPagination.page = newPage;

        const { employeesToPreview } = await this.employeeFilterPreviewService.preview(
            this.employeeFilter,
            this.auth.company,
            newPage
        );

        this.employeesToPreview = employeesToPreview;
    }

    hideEmployeesMatched(): void {
        this.showEmployeesMatched = false;
    }

    async attachEmployeeFilters(): Promise<void> {
        const toAttach = this.getValuesToAttach();
        const toDetach = this.getValuesToDetach();

        this.employeeFilter.attachEmployeeFilters(this.employeeFilter, toAttach);
        this.employeeFilter.detachEmployeeFilters(this.employeeFilter, toDetach);

        const employeeFilter = await this.employeeFilter.param('company', this.auth.company.id).save();

        this.employeeCriteriaSelection?.clearAttachDetachFilters();
        this.employeeFilter = employeeFilter;
    }

    getValuesToAttach(): EmployeeSelectionCriteriaFields {
        return this.sendToAll ? setNoFilters : this.employeeCriteriaSelection.getValuesToAttach();
    }

    getValuesToDetach(): EmployeeSelectionCriteriaFields {
        return this.sendToAll ? setNoFilters : this.employeeCriteriaSelection.getValuesToDetach();
    }

    onCancel(): void {
        this.redirect();
    }

    onSelectionChange(): void {
        this.previewSelectedEmployees();
    }

    async sendTestAnnouncement(announcementForm: AnnouncementForm): Promise<void> {
        this.isLoading = true;
        this.announcement = announcementForm.announcement;
        this.announcement.isTest = true;

        try {
            await this.announcement.param('company', this.auth.company.id).save();
            this.testAnnouncementSent = true;
        } catch (err) {
            this.notify.error(err || 'dashboard.sendAnnouncementError');
        }

        this.isLoading = false;
        this.announcement.isTest = false;
    }

    closeTestAnnouncementModal(): void {
        this.testAnnouncementSent = false;
    }

    openDashboardInNewTab(): void {
        window.open(this.router.parseUrl('/dashboard').toString(), '_blank');
    }

    private redirect(): void {
        this.router.navigate(['/dashboard']);
    }
}
